/**
 * Unified date formatting utilities for Qwoted
 *
 * Pattern-based formatting: use qformat() with DATE_FORMATS constants.
 * Behavioral formatting: use the named functions (formatRelativeVerbose, formatTimeAgo, etc.)
 *
 * @example
 * import { qformat, DATE_FORMATS, formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";
 *
 * qformat(notification.created_at, DATE_FORMATS.dateTime) // "Dec 1, 2024, 2:30 PM"
 * qformat(request.posted_at, DATE_FORMATS.short)          // "Fri, Dec 1"
 * qformat(date, "MMM yyyy")                               // custom pattern
 * formatRelativeVerbose(user.last_sign_in_at)              // "3 days ago"
 */

import { differenceInDays, differenceInMilliseconds, format, isPast, parseISO } from "date-fns";

/**
 * Parse a date input into a Date object. Bare timestamps (without timezone
 * offset) are assumed UTC — a console warning fires in non-production builds
 * so developers can fix the source.
 *
 * @param {string|Date|number} dateInput - Date string, Date object, or Unix timestamp
 * @returns {Date} Parsed Date object
 */
function toDate(dateInput) {
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput === "string") {
    // Date-only strings (YYYY-MM-DD) are calendar dates, not timestamps.
    // Parse them as local dates — appending "Z" would shift the day in western timezones.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return parseISO(dateInput);
    }

    // If no timezone indicator on a timestamp, assume UTC and warn in dev.
    // Must use $ anchor — unanchored [+-]\d{2} false-positives on "-03" in date portion.
    const hasOffset = /Z$|[+-]\d{2}(:\d{2})?$/.test(dateInput);
    if (!hasOffset) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[toDate] Bare timestamp without timezone offset: "${dateInput}". Assuming UTC. Fix the source.`);
      }
      return parseISO(`${dateInput}Z`);
    }
    return parseISO(dateInput);
  }
  return new Date(dateInput);
}

// ---- Format patterns ----

export const DATE_FORMATS = {
  // Dates
  dayOfWeek: "EEEE", // Friday
  date: "MMM d, yyyy", // Dec 1, 2024
  dateNoYear: "MMM d", // Dec 1
  isoDate: "yyyy-MM-dd", // 2024-12-01
  short: "EEE, MMM d", // Fri, Dec 1
  shortYear: "EEE, MMM d, yyyy", // Mon, Dec 1, 2024
  ordinal: "EEEE, MMM do", // Friday, May 9th
  ordinalWithYear: "EEEE, MMM do, yyyy", // Friday, May 9th, 2027

  // Times
  time: "h:mm a", // 2:30 PM
  timeLower: "h:mm aaa", // 2:30 pm

  // Date + Time
  dateTimeShort: "MMM d, h:mm a", // Dec 1, 2:30 PM
  dateTime: "MMM d, yyyy, h:mm a", // Dec 1, 2024, 2:30 PM
  dateTimeLower: "MMM d, yyyy, h:mm aaa", // Dec 1, 2024, 2:30 pm
  dateTimeLong: "MMMM d, yyyy, hh:mm a", // December 1, 2024, 02:30 PM
  dateTimeLongLower: "MMMM d, yyyy, hh:mm aaa", // December 1, 2024, 02:30 pm
  timestamp: "Pp", // 12/15/2024, 2:30 PM

  // For <input type="datetime-local"> min/value attributes
  inputDateTimeLocal: "yyyy-MM-dd'T'HH:mm", // 2024-12-01T14:30
};

/**
 * Format a date using a date-fns pattern string, with null guard and string parsing.
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} pattern - date-fns format pattern (use DATE_FORMATS constants or a custom string)
 * @param {string} [fallback="N/A"] - Return value when dateInput is null/undefined
 * @returns {string} Formatted date string
 */
export function qformat(dateInput, pattern, fallback = "N/A") {
  if (!dateInput) return fallback;
  return format(toDate(dateInput), pattern);
}

/**
 * The viewer's short time-zone label for a given instant ("EDT", "PST", "GMT+3"),
 * DST-correct for that date. Pair with qformat when the zone matters (V1 parity).
 *
 * @param {string|Date} [dateInput=new Date()] - The instant whose zone label to resolve
 * @returns {string} Short zone name, or "" when unresolvable
 */
export function timeZoneAbbr(dateInput = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(toDate(dateInput));
  return parts.find((part) => part.type === "timeZoneName")?.value || "";
}

// ---- Behavioral functions (real logic, not just format patterns) ----

/**
 * Format a stored calendar date (e.g. an employment start/end) as "Feb 2023".
 *
 * Deliberately extracts year/month from the STRING rather than going through
 * toDate()/qformat(): these values arrive as UTC-midnight timestamps, and
 * rendering that instant in the viewer's time zone would show the previous
 * month ("Jan 2023") anywhere west of UTC. The stored calendar month is the
 * truth; the viewer's clock is irrelevant.
 *
 * @param {string} value - ISO date or timestamp string ("2023-02-01...")
 * @returns {string|null} "Feb 2023", or null for falsy/unparseable input
 */
export function formatMonthYear(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month] = match;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/**
 * Format a calendar date range as "Feb 2023 – Present" / "Feb 2023 – Jun 2024".
 * A null end date reads as "Present"; a null start collapses to just the end.
 *
 * @param {string} [from] - ISO date/timestamp string
 * @param {string} [to] - ISO date/timestamp string
 * @returns {string} The formatted range
 */
export function formatDateRange(from, to) {
  const start = formatMonthYear(from);
  const end = formatMonthYear(to) || "Present";
  return start ? `${start} – ${end}` : end;
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Calculate the difference between a date and now in days
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {number} Number of full days since the date
 */
export function daysSince(dateInput) {
  if (!dateInput) return Infinity;
  const date = toDate(dateInput);
  return differenceInDays(new Date(), date);
}

/**
 * Format a date as verbose relative time for admin panels and detailed views
 *
 * Past dates: "Today", "Yesterday", "3 days ago", "2 weeks ago", "1 year ago"
 * Future dates: "Today", "Tomorrow", "3 days", "2 weeks", "1 year"
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Verbose relative time string, or "Never" if null
 */
export function formatRelativeVerbose(dateInput) {
  if (!dateInput) return "Never";

  const date = toDate(dateInput);
  const now = new Date();
  const diffMs = now - date;
  const absDays = Math.floor(Math.abs(diffMs) / MS_PER_DAY);
  const future = diffMs < 0;

  const suffix = future ? "" : " ago";
  const pluralize = (count, singular) =>
    count === 1 ? `${count} ${singular}${suffix}` : `${count} ${singular}s${suffix}`;

  if (absDays === 0) return "Today";
  if (absDays === 1) return future ? "Tomorrow" : "Yesterday";
  if (absDays < 7) return pluralize(absDays, "day");
  if (absDays < 30) return pluralize(Math.floor(absDays / 7), "week");
  if (absDays < 365) return pluralize(Math.floor(absDays / 30), "month");
  return pluralize(Math.floor(absDays / 365), "year");
}

/**
 * Format a date as compact relative time with "ago" suffix for recent activity
 * Returns: "just now", "3m ago", "2h ago", "5d ago", or falls back to absolute date for >30 days
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Compact time-ago string
 */
export function formatTimeAgo(dateInput) {
  if (!dateInput) return "—";

  const date = toDate(dateInput);
  const now = new Date();

  const diffMs = differenceInMilliseconds(now, date);
  const diffMin = Math.floor(diffMs / MS_PER_MINUTE);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;

  return qformat(dateInput, DATE_FORMATS.date);
}

/**
 * Format a date as compact relative time in either direction, single-letter
 * units (m/h/d). Past: "just now", "3m ago", "2h ago", "5d ago". Future:
 * "in <1m", "in 3m", "in 2h", "in 5d". Designed for table cells alongside a
 * `title` attribute that exposes the absolute time on hover.
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Compact relative string, or "—" if dateInput is null
 */
export function formatRelativeCompact(dateInput) {
  if (!dateInput) return "—";

  const date = toDate(dateInput);
  const now = new Date();
  const diffMs = differenceInMilliseconds(date, now);
  const future = diffMs > 0;
  const absMs = Math.abs(diffMs);

  const absMin = Math.floor(absMs / MS_PER_MINUTE);
  if (absMin < 1) return future ? "in <1m" : "just now";
  if (absMin < 60) return future ? `in ${absMin}m` : `${absMin}m ago`;

  const absHours = Math.floor(absMin / 60);
  if (absHours < 24) return future ? `in ${absHours}h` : `${absHours}h ago`;

  const absDays = Math.floor(absHours / 24);
  return future ? `in ${absDays}d` : `${absDays}d ago`;
}

/**
 * Format a deadline with context-aware relative time
 * - Past dates: "Friday, May 9th, 9 months ago"
 * - Upcoming within a week: "Friday, May 9th (3d)"
 * - Other future dates: "Friday, May 9th"
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted deadline string
 */
export function formatDeadline(dateInput) {
  if (!dateInput) return "—";
  const date = toDate(dateInput);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / MS_PER_DAY);

  const formatted = qformat(date, DATE_FORMATS.ordinal, "—");

  if (diffMs < 0) {
    return `${formatted}, ${formatRelativeVerbose(date).toLowerCase()}`;
  }

  if (diffDays > 0 && diffDays <= 7) {
    return `${formatted} (${diffDays}d)`;
  }

  return formatted;
}

/**
 * Check if a deadline date is in the past
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {boolean} True if deadline has passed
 */
export function isDeadlinePast(dateInput) {
  if (!dateInput) return false;
  return isPast(toDate(dateInput));
}

/**
 * Calculate time remaining until a future date
 * @param {string|Date} futureDate - The future date
 * @returns {Object} Object with days, hours, minutes, seconds remaining
 */
export function getTimeRemaining(futureDate) {
  const now = new Date();
  const target = toDate(futureDate);
  const totalMs = differenceInMilliseconds(target, now);

  if (totalMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
  }

  const days = Math.floor(totalMs / MS_PER_DAY);
  const hours = Math.floor((totalMs % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((totalMs % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((totalMs % MS_PER_MINUTE) / MS_PER_SECOND);

  return { days, hours, minutes, seconds, totalMs };
}

/**
 * Format time remaining into a readable string
 * @param {Object} timeRemaining - Object from getTimeRemaining
 * @returns {string} Formatted time remaining string
 */
export function formatTimeRemaining(timeRemaining) {
  const { days, hours, minutes, seconds } = timeRemaining;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}
